<!-- MARK: Tutorial -->
<app-intro-container>
    <app-section sectionTitle="TUTORIAL" [isWarning]="true">
        <p>
            Make sure you check out the
            <a routerLink="/articles/view/albion-online-black-market-flipper-tutorial">complete Albion Flipper
                tutorial</a>.
        </p>
    </app-section>

    <app-section sectionTitle="WHAT IS THIS TOOL?">
        <p>
            This tool enables you to perform instant, one-time market flips for quick profits. Remember,
            <b>you need to scan the market to find profitable flips!</b> Be sure to download the Data Client
            <a href="https://github.com/JPCodeCraft/AlbionDataAvalonia" target="_blank">here</a> to scan the market.
        </p>
        <p>
            For more information and tips, join our <a href="https://discord.com/invite/BPmDE3zznH"
                target="_blank">Discord</a> server or check out the
            <a routerLink="/articles/view/albion-online-black-market-flipper-tutorial">complete Albion Flipper
                tutorial</a>.
        </p>
        <p>
            You can also Subscribe to our <a href="https://www.youtube.com/@AlbionFreeMarket" target="_blank">YouTube
                channel</a> for more flipping videos.
        </p>
    </app-section>

    <app-section sectionTitle="KEY FEATURES">
        <div class="features-grid">
            <app-feature featureTitle="📈 Instant Profit Flips">
                <p>Obtain a list of instant market flips by analyzing current market prices, including expenses, income,
                    and
                    profit per transaction.</p>
            </app-feature>
            <app-feature featureTitle="🌐 Market Data Integration">
                <p>Utilize real-time data from The Albion Online Data Project to identify profitable flipping
                    opportunities across various markets.</p>
            </app-feature>
            <app-feature featureTitle="🔒 Private Flips">
                <p>Access exclusive flipping opportunities based solely on your own market data, minimizing competition
                    and reducing the risk of failed flips.</p>
            </app-feature>
            <app-feature featureTitle="⚙️ Upgrade Flip Analysis">
                <p>Evaluate potential profits from upgrading items using runes, souls, and relics, considering material
                    costs and market demand.</p>
            </app-feature>
            <app-feature featureTitle="📊 Flipper Statistics">
                <p>Access comprehensive charts displaying the total value of consumed flips per hour, aiding in
                    strategic planning and market timing.</p>
            </app-feature>
        </div>
    </app-section>

    <app-section sectionTitle="HOW TO USE">
        <app-section-subtitle>Understanding Black Market Flipping</app-section-subtitle>
        <app-subsection subSectionTitle="What is a Flip?">
            <p>A flip involves purchasing an item from a standard market and selling it to a buy order, usualy on the
                Black
                Market, for profit. These are single-use, instant transactions.</p>
        </app-subsection>
        <app-subsection subSectionTitle="Data Sources">
            <p>Price data is sourced from The Albion Online Data Project (AODP). To contribute data, use the official
                AODP client or the Albion Free Market Data Client, which offers a graphical interface. You need
                to use a data client to succesfully flip!</p>
        </app-subsection>

        <app-section-subtitle>Using the Black Market Flipper Tool</app-section-subtitle>
        <app-subsection subSectionTitle="Finding Flips">
            <ul>
                <li><strong>Market Scanning:</strong> Use the data client to upload current market prices. The system
                    compares sell orders from standard markets with buy orders on the Black Market to identify
                    profitable flips.</li>
                <li><strong>Data Freshness:</strong> Sell orders are held for 60 minutes and buy orders for 30 minutes
                    to minimize false positives.</li>
                <li><strong>Three Steps:</strong> Flipping involves three key steps:
                    <ul>
                        <li><strong>Scanning the City Market:</strong> Scan the market in your chosen
                            city.</li>
                        <li><strong>Scanning the Black Market:</strong> Scan the Black
                            Market.</li>
                        <li><strong>Executing the Flip:</strong> Purchase items from the standard market and sell them
                            to the Black Market as quickly as possible to capitalize on identified opportunities.</li>
                    </ul>
                </li>
            </ul>
        </app-subsection>
        <app-subsection subSectionTitle="Consuming Flips">
            <ul>
                <li><strong>Marking as Consumed:</strong> When you execute a flip, mark it as consumed to track your
                    transactions and update the system.</li>
                <li><strong>Visibility:</strong> Consumed flips remain visible to all users to prevent misuse and ensure
                    transparency.</li>
            </ul>
        </app-subsection>

        <app-section-subtitle>Flipping Strategies</app-section-subtitle>
        <app-subsection subSectionTitle="Safe Flipping">
            <p><strong>Caerleon Only:</strong> Buy items in Caerleon and sell directly to the Black Market. This method
                avoids red zones and is safer but typically yields lower profits (e.g., 100k profit flips are common;
                1M+ are rare).</p>
        </app-subsection>
        <app-subsection subSectionTitle="Riskier Flipping">
            <p><strong>Royal Cities to Black Market:</strong> Purchase items in royal cities and transport them to the
                Black Market. This approach involves traversing red zones, increasing risk but also potential profit.
                Brecilien offers high profitability but with significant danger.</p>
        </app-subsection>

        <app-section-subtitle>Flipper Statistics</app-section-subtitle>
        <app-subsection subSectionTitle="Activity Monitoring">
            <p>The flipper page displays a chart of total value of consumed flips per hour. Highlighted areas represent
                weekends. Use this to gauge recent flipping activity and identify optimal times for profitable flips.
            </p>
        </app-subsection>
    </app-section>
</app-intro-container>